import { ContactType, IContact } from "./Contact";
import { IDocument } from "./Document";
import { IInvoice } from "./Invoice";
import { IJob } from "./Job";
import { BodyCondition, EngineCondition, IKanbanTodoItem, IVehicle } from "./Vehicle";
import { BaseEntity, DefaultResponse, DefaultResponseWithData } from "./system";

export interface ILeadResponse {
  lastExported: string | null;
  lastImported: string | null;
  leads: ILead[];
}

export interface INewLead {
  CCID: string | null;
  website: LeadSource | null;
  source: string | null;
  price: number | null;
  commission: number | null;
  vehicleGrade: string | null;
  vehicle: {
    vrm: string | null;
    make: string | null;
    model: string | null;
    postcode: string | null;
    line1: string | null;
    line2: string | null;
    line3: string | null;
    line4: string | null;
    locality: string | null;
    town: string | null;
    county: string | null;
    isDropOff: boolean;
  };
  contact: {
    contactId: string;
    fullname: string | null;
    email: string | null;
    telephone1: string | null;
    telephone2: string | null;
  };
}

export interface ISingleLeadRespose extends DefaultResponseWithData<ILead> { }

// Complete also raises the self-billed invoice. The lead can complete while that fails, so
// success is the lead and invoiceCreated is the invoice.
export interface ICompleteLeadResponse extends DefaultResponse {
  invoiceCreated: boolean;
  invoiceMessage: string | null;
}

export interface IAllLeadsResponse
  extends DefaultResponseWithData<ILeadResponse> { }

export interface IPaymentsLeadsResponse
  extends DefaultResponseWithData<ILead[]> { }

export interface IUnscheduledLeadsResponse
  extends DefaultResponseWithData<ILead[]> { }

export enum LeadStatus {
  "Awaiting Contact",
  "Scheduled",
  "Cancelled",
  "Reschedule",
  "Collected",
  "Awaiting Payment",
  "Awaiting COD",
  "Customer to Call Back",
  "Completed",
  "All",
  "Call List",
  "Allocated",
}

export enum LeadSource {
  Manual,
  SCC,
  CTB,
  RMC,
  Motorwise,
  All,
  SellAVehicle,
  Widget,
  RemoveMyCar,
  CarCo,
  C2B,
  B2B,
  SMCO,
}

export enum LeadDocumentFilter {
  "Past Hour",
  "Today",
  "This Week",
  "Past 7 Days",
  "This Month",
  "Past 30 Days",
  "All",
}

export enum LeadPaymentMethod {
  "Bank Transfer",
  "Cheque",
  "Cash"
}

export enum PaymentStatus {
  "Not Started",
  "Raised",
  "Sent",
  "Failed",
  "Approved",
}

export enum LeadQualified {
  "No Reply",
  "Qualified",
  "Stop",
  "No Match",
}

export interface ILead extends BaseEntity {
  tripId: string | null;
  // The trip's timeslot (from the trip manager) - returned by the single lead and partial lead endpoints
  tripTimeSlotStart?: string | null;
  tripDuration?: number | null;
  truckId: string | null;

  contactId: string | null;

  driverId: string | null;
  driver: IContact | null;

  invoiceId: string | null;
  invoice: IInvoice | null;

  ccid: string | null;
  contact: IContact | null;
  officeNotes: string | null;
  driverNotes: string | null;
  yardNotes: string | null;
  vehicleId: string | null;
  vehicle: IVehicle | null;
  status: LeadStatus;
  callListStatus: CallListStatus | null;
  website: LeadSource;
  source: string | null;
  price: number;
  applyVAT: boolean;
  priceWithVAT: number;
  commission: number;
  notes: string;
  completedOn: string;
  qualified: LeadQualified;
  exportedOn: string | null;
  partsLookupRequestId: string | null;
  cancelledReason: string | null;
  cancelledBy: string | null;
  cancelledOn: string | null;
  paymentStatus: PaymentStatus;
  paymentMethod: LeadPaymentMethod;
  paymentMessage: string | null;
  paymentRaisedBy: string | null;
  paymentApprovedBy: string | null;
  paymentConfirmedOn: string;
  paymentType: string | null;
  accountName: string | null;
  accountNumber: string | null;
  sortCode: string | null;
  paymentReference: string | null;
  cardNumber: string | null;
  payeeDOB: string | null;
  chequeName: string | null;
  chequeNumber: string | null;
  premiumPriceUsed: string | null;
  scheme: string | null;
  vehicleGrade: string | null;
  callCount: number;
  callCountUpdatedOn: string | null;
  callCountUpdatedBy: string | null;
  documentsRequested: string | null;
  order: number;
  jobs: IJob[] | null;
  catalyticAveragePrice: number | null;
  correctedVrm: string | null;
  allocatedToCustomerId: string | null;

  deleted: string | null;
  deletedBy: string | null;

  // Widget
  quoteType: QuoteType;
  quoteTabType: QuoteTabType;
  quoteLocationId: string | null;

  roadSidePayment: boolean;
  fastPayment: boolean;
  collectionOptionId: string | null;
  collectionOptionName: string | null;
  collectionOptionPrice: number;
  accessToVehicle: boolean;
  v5c: boolean;
  isCollection: boolean;
  preferredCollectionDate: string | null;
  // The condition the customer described on THIS lead's quote, frozen by the widget when the lead
  // was created. A registration shares one vehicle row and a later lead overwrites its condition
  // fields, so `vehicle.engineCondition` and friends are the car's latest known state rather than
  // what this customer said. Null on leads created before the widget captured these — fall back to
  // the vehicle then, and label it, rather than presenting it as the customer's answer.
  quotedMileage: number | null;
  quotedEngineCondition: EngineCondition | null;
  quotedBodyCondition: BodyCondition | null;
  quotedAlloyWheels: boolean | null;
  // Google Ads click identifier, present only when the quote came from a paid click
  gclid: string | null;
  // json string of calculation data
  calculationJson: string | null;
  collectionCustomerId: string | null;
  dropOffCustomerId: string | null;

  // Second booking for a vehicle, flagged by the widget. The API only fills these for SAV.
  routingOutcome?: LeadRoutingOutcome;
  /** Why the lead went where it did. Null unless routingOutcome is not Normal. */
  routingReason?: string | null;
  /** The booking that already held this registration - usually in another yard's Wilma. */
  duplicateOfLeadId?: string | null;
  /** Who holds duplicateOfLeadId. Lead detail only; null on leads flagged before it was sent. */
  duplicateOfCustomerId?: string | null;
  /** Whether duplicateOfLeadId is a lead in this Wilma, so it can be linked. Lead detail only. */
  duplicateOfLeadIsLocal?: boolean;

  tellerooTransactionId: string;

  documents: IDocument[] | null;
}

/**
 * Whether the widget sent a lead to the customer who won its quote, and if not, why not. Numeric
 * values come from the API, so the order must not change.
 */
export enum LeadRoutingOutcome {
  Normal = 0,
  /** Marketplace quote for a car another booking already held, so SAV took it to arbitrate. */
  DivertedDuplicateVrm = 1,
  /** Own-website quote for a car another booking already held. Left with that customer. */
  FlaggedDuplicateVrm = 2,
  /** Marketplace lead SAV holds for allocation. The only booking for the car - not a duplicate. */
  HeldForAllocation = 3,
}

export const isDuplicateRouting = (outcome?: LeadRoutingOutcome | null) =>
  outcome === LeadRoutingOutcome.DivertedDuplicateVrm ||
  outcome === LeadRoutingOutcome.FlaggedDuplicateVrm;

export enum CallListStatus
{
  CallListOne     = 0,
  EmailListOne    = 1,
  CallListTwo     = 2,
  EmailListTwo    = 3,
  CallListThree   = 4,
  EmailListThree  = 5,
  ManagerApproval = 6,
  Archived        = 7
}

export enum QuoteType {
  ScrapExport,
  Salvage,
  SmartPrice
}

// Read-only historical snapshot of the address quoted when the lead came in.
export interface IQuoteLocation extends BaseEntity {
  fullname: string;
  telephone1: string;
  telephone2: string;
  email: string;
  line1: string | null;
  line2: string | null;
  line3: string | null;
  line4: string | null;
  locality: string | null;
  town: string | null;
  county: string | null;
  postcode: string | null;
  longitude: number;
  latitude: number;
}

export interface ISingleQuoteLocationResponse extends DefaultResponseWithData<IQuoteLocation> { }

export interface ICalculationData {
  salvageCalculations: ICalculation[];
  tradePoorCalculations: ICalculation[];
  salvageVoucherCalculation: ICalculation;
  // scrap
  postcodeCalculations: ICalculation[];
  catalyticCalculations: ICalculation[];
  engineCalculations: ICalculation[];
  vfeCalculations: ICalculation[];
  generalCalculations: ICalculation[];
  genericCalculations: ICalculation[];
  additionalCalculations: ICalculation[];
  scrapVoucherCalculation: ICalculation;
  // lookup
  partsLookupCalculations: ICalculation[];
  partsLookupGenericCalculations: ICalculation[];
  // other
  dropOff: number;
  payment: ICalculation;
  collectionOption: ICalculation;
}

export interface ICalculation {
  name: string;
  isFixed: boolean;
  value: number;
  baseValue: number;
  isAddition: boolean;
  customCalculation: string;
}


export interface ILeadStats {
  awaitingContact: number;
  customerToCallBack: number;
  scheduled: number;
  reschedule: number;
  collected: number;
  collectedToday: number;
  awaitingCod: number;
  awaitingPayment: number;
  cancelled: number;
  completed: number;
  completedToday: number;
}


export interface ILeadActionsResponse extends DefaultResponseWithData<ILeadAction[]> { }
export interface ILeadActionResponse extends DefaultResponseWithData<ILeadAction> { }

export interface ILeadAction {
  id: string;
  leadId: string;
  actionType: LeadActionType;
  description: string;
  actionedBy?: string | null;
  created: string;
}

export enum LeadActionType
{
  Generic,
  Deleted,
  Cancelled,
  PaymentStatusChanged,
  StatusChanged,
  CallListStatusChanged,
  PriceChanged,
}

export enum QuoteTabType {
  Quotes,
  Atf,
  HighValue,
  HighPriority,
  ManagerAssistance
}

// Columns of the collections board (mirrors backend LeadPipelineStage).
// Ordinals are the column keys — only ever append new stages at the end.
export enum LeadPipelineStage {
  AwaitingCollection,
  Collected,
  AwaitingPayment,
  AwaitingCod,
  Complete,
  // Sits between AwaitingCollection and Collected on the board; appended so the ordinal (the
  // column key from the API) stays stable. Board order lives in the board's column array.
  Booked,
}

// A single card on the collections board: a lead plus the funnel stage (column) it sits in.
export interface IKanbanLead {
  stage: LeadPipelineStage;
  lead: ILead;
  todos: IKanbanTodoItem[];
  driverName: string | null;
  checkInCompletedCount: number;
  checkInTotalSteps: number;
}

export interface ILeadPipelineCount {
  stage: LeadPipelineStage;
  count: number;
}

export interface ILeadKanbanResponse {
  // Full total per stage — cards are capped per column, so these are what the headers show.
  counts: ILeadPipelineCount[];
  cards: IKanbanLead[];
  completeWindowDays: number;
}
